// frontend/src/pages/superadmin/LicensesManager.jsx
import React, { useState, useEffect } from 'react';
import { Key, CheckCircle, XCircle, RefreshCw, Search } from 'lucide-react';
import { getAirports, getAirportLicense, resetAirportLicenseKey } from '../../services/superAdminApi';

const LicensesManager = () => {
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [resetting, setResetting] = useState(null);

  useEffect(() => {
    loadAirports();
  }, []);

  const loadAirports = async () => {
    setLoading(true);
    try {
      const response = await getAirports(1, 100, searchTerm);
      setAirports(response.data.airports || []);
    } catch (error) {
      console.error('Error loading airports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetLicense = async (airportId, airportName) => {
    if (window.confirm(`Reset license key for ${airportName}?`)) {
      setResetting(airportId);
      try {
        const response = await resetAirportLicenseKey(airportId);
        alert(`New license key: ${response.data.new_license_key}`);
        await loadAirports();
      } catch (error) {
        console.error('Error resetting license:', error);
        alert('Error resetting license');
      } finally {
        setResetting(null);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">License Management</h1>
          <p className="text-gray-400 text-sm">Manage airport licenses and activation keys</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Search airports..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white"
        />
      </div>

      {/* Licenses Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800 border-b border-gray-700">
            <tr>
              <th className="p-4 text-left text-gray-400">Airport</th>
              <th className="p-4 text-left text-gray-400">License Key</th>
              <th className="p-4 text-left text-gray-400">Agents</th>
              <th className="p-4 text-left text-gray-400">Expires</th>
              <th className="p-4 text-left text-gray-400">Status</th>
              <th className="p-4 text-left text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {airports.map((airport) => (
              <tr key={airport.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                <td className="p-4">
                  <p className="text-white font-medium">{airport.name}</p>
                  <p className="text-gray-500 text-xs">{airport.subdomain}.aerotrick.com</p>
                </td>
                <td className="p-4">
                  <code className="text-blue-400 text-sm bg-gray-900 px-2 py-1 rounded">
                    {airport.license_key?.substring(0, 30)}...
                  </code>
                </td>
                <td className="p-4">
                  <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                    {airport.used_agents || 0}/{airport.max_agents || 5}
                  </span>
                </td>
                <td className="p-4 text-gray-300 text-sm">
                  {formatDate(airport.license_expires)}
                </td>
                <td className="p-4">
                  {airport.license_valid ? (
                    <span className="flex items-center gap-1 text-green-400 text-xs">
                      <CheckCircle className="w-3 h-3" /> Valid
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-400 text-xs">
                      <XCircle className="w-3 h-3" /> Invalid
                    </span>
                  )}
                </td>
                <td className="p-4">
                  <button
                    onClick={() => handleResetLicense(airport.id, airport.name)}
                    disabled={resetting === airport.id}
                    className="p-1.5 hover:bg-gray-700 rounded-lg text-yellow-400 disabled:opacity-50"
                    title="Reset License Key"
                  >
                    {resetting === airport.id ? (
                      <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LicensesManager;